import { Divider } from '@ps-design-system/components/Divider';
import { Form } from '@ps-design-system/components/Form';
import { Text } from '@ps-design-system/components/Text';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import {
  getCurrencyFromListing,
  getErrorMsg
} from '../../checkout-utils';
import { useCheckoutContext } from '../../CheckoutContextProvider';
import { BookingRequestPriceSummary } from '../../price-summaries/BookingRequestPriceSummary/BookingRequestPriceSummary';
import { SplitPaymentPriceSummary } from '../../price-summaries/SplitPaymentPriceSummary/SplitPaymentPriceSummary';
import { fetchCustomerCredits } from '../../services/payment-service';
import { PaymentOptionField } from '../BookingDetailsForm/PaymentOptionField';
import { CreditsField } from './CreditsField';
import { PaymentMethodField } from './PaymentMethodField';
import { PaymentElement } from './PaymentElement';
import { useStripeElementsContext } from '../../StripeElementsContextProvider';

export const PaymentForm = ({ accessToken, SSOid, intl, urlPrefix }) => {
  const {
    paymentForm,
    quote,
    listing,
    isFetchingQuote,
    isSubmittingRequest
  } = useCheckoutContext();

  const { availablePaymentMethods } = useStripeElementsContext();

  const { booking } = quote?.spaceRentalContract?.meta || {};

  const [customerCredits, setCustomerCredits] = React.useState(null);
  const [customerCreditsErrorMsg, setCustomerCreditsErrorMsg] =
    React.useState('');

  const splitPaymentSelected =
    paymentForm?.watch('selectedPaymentOption') === 'pay_partial';

  const selectedPaymentMethod =
    paymentForm?.watch('selectedPaymentMethod');

  React.useEffect(() => {
    (async () => {
      try {
        const creditsRes = await fetchCustomerCredits(accessToken, SSOid);
        setCustomerCredits(creditsRes);
      } catch (error) {
        setCustomerCreditsErrorMsg(getErrorMsg(error));
      }
    })();
  }, []);

  const subtotalRemaining = quote?.guestPaymentDetails?.subtotal_remaining;
  const totalCreditsApplied = paymentForm.watch('totalCreditsApplied');
  const showPaymentMethodField = availablePaymentMethods?.length > 1;

  return (
    !!paymentForm && (
      <Form testId="payment-form" spacing={0} {...paymentForm}>
        {quote?.splitPaymentPermitted && (
          <>
            <Divider />
            <div style={{ marginBottom: '24px' }}>
              <Text
                typeface="headline-3-b"
                element="h4"
                style={{ margin: '0 0 24px 0' }}
              >
                <FormattedMessage
                  id="booking_details_form.payment_option"
                  defaultMessage="Choose how to pay"
                />
              </Text>

              <PaymentOptionField disabled={totalCreditsApplied > 0 || isSubmittingRequest} />
            </div>
          </>
        )}

        {/* credits only allowed in US  */}
        {listing?.country?.toLowerCase() === 'us' && customerCredits > 0 && (
          <>
            <Divider />
            <CreditsField
              errorMsg={customerCreditsErrorMsg}
              customerCredits={customerCredits}
            />
          </>
        )}

        <div className="d-lg-none">
          <Divider />

          <Text
            typeface="headline-3-b"
            element="p"
            style={{ margin: '0 0 24px 0' }}
          >
            <FormattedMessage
              id="payment_section.price_details"
              defaultMessage="Price details"
            />
          </Text>

          <BookingRequestPriceSummary
            urlPrefix={urlPrefix}
            SSOid={SSOid}
            showSkeleton={isFetchingQuote}
            showTotalBorder={false}
            accessToken={accessToken}
            tooltipAlign="start"
          />

          {splitPaymentSelected && (
            <div style={{ marginTop: '24px' }}>
              <SplitPaymentPriceSummary
                splitPaymentCalculations={
                  quote?.masterContract?.calculations?.guest?.split_payment
                }
                splitPaymentChargeDate={
                  quote?.masterContract?.split_payment_charge_date
                }
                currency={getCurrencyFromListing(listing)}
              />
            </div>
          )}
        </div>

        <Divider />

        <div
          style={
            subtotalRemaining <= 0
              ? {
                  // hiding rather than unmounting to ensure we're only creating one payment intent for the session (its created when StripePaymentElementWithPaymentIntent mounts)
                  display: 'none'
                }
              : {}
          }
        >
          <div style={{ marginBottom: '24px' }}>
            <Text element="h4" typeface="headline-3-b" style={{ margin: '0' }}>
              <FormattedMessage
                id="payment_section.title"
                defaultMessage="Payment"
              />
            </Text>
          </div>
        </div>

        {showPaymentMethodField &&
          <div style={{ marginBottom: '24px' }}>
            <PaymentMethodField intl={intl} disabled={!booking || isSubmittingRequest} />
          </div>
        }

        <div className={classnames({hidden: selectedPaymentMethod !== 'card'} )}>
          <PaymentElement disabled={!booking || isSubmittingRequest} intl={intl} />
        </div>

        <Text
          element="p"
          typeface="body-1"
          style={{ color: '#6A6E7A', margin: '16px 0 0 0' }}
        >
          <FormattedMessage
            id="booking_details_form.payment_option_note"
            defaultMessage="By requesting to book, a hold will be placed on your card. You won’t be charged until {hostName} accepts your request (usually within 24 hours)."
            values={{ hostName: booking?.host?.name?.first }}
          />
        </Text>
      </Form>
    )
  );
};

PaymentForm.displayName = 'PaymentForm';
PaymentForm.propTypes = {
  customerPaymentMethods: PropTypes.array,
  accessToken: PropTypes.string,
  SSOid: PropTypes.string,
  listingCountry: PropTypes.string,
  currency: PropTypes.string,
  hostName: PropTypes.string,
  intl: PropTypes.object,
  urlPrefix: PropTypes.string
};
